import { z } from 'zod';

export type ResultObject<T extends z.ZodTypeAny> = {
  [K in keyof z.infer<T>]: z.infer<T>[K]
} & { generated?: boolean };

const extractSchema = (s: z.ZodTypeAny) => {
  if (s instanceof z.ZodObject) {
    return s;
  }

  if (s instanceof z.ZodEffects) {
    return extractSchema(s._def.schema);
  }

  return undefined;
};

/**
 *
 * @description This function takes a given zod schema, and builds an object that exactly matches the schema definition, including objects nested
 * in other objects or arrays. The root node of the schema MUST be z.object(). If a schema field is provided with a value via .default(), that value
 * will be used for that field in the return object
 * Otherwise, the default values are as follows:
 * - strings: undefined
 * - numbers: undefined
 * - booleans: false
 * - objects: an object that matches the schema field, with primitive values set as shown above
 * - arrays of objects: an array with a single object that matches the schema, with primitive values as shown above
 * - arrays of primitive values: an empty array
 * @param schema the zod schema you want to use to create an object
 * @param overrides Optional object to provide your own specific values for a given object field or fields. Object key must match a root path in your schema
 * @param addGeneratedIdentifier adds a `generated: true` field to objects that can be used to differentiate automatically generated objects from user selected ones
 * @returns an object with a shape that matches your input schema
 */
export const createObjectFromSchema = <T extends z.ZodTypeAny, U extends object = {[K in keyof z.infer<T>]?: z.infer<T>[K] }>(schema: T, overrides?: U, addGeneratedIdentifier?: boolean): ResultObject<T> => {
  const _schema = extractSchema(schema);

  if (!_schema) {
    throw new Error('Object supplied to function is not a valid zod object schema');
  }

  const isPrimitiveSchema = (test: z.ZodTypeAny) => (test instanceof z.ZodString) || (test instanceof z.ZodNumber) || (test instanceof z.ZodBoolean);

  const getSchemaDefault = (defaultSchema: z.ZodDefault<any>) => {
    const value = defaultSchema._def.defaultValue();
    return value;
  };

  const getObjectDefault = (objectSchema: z.ZodObject<any>) => Object.fromEntries(
    Object.keys(objectSchema.shape).map(key => [key, processSchemaField(objectSchema.shape[key])]),
  );

  const getArrayDefault = (arraySchema: z.ZodArray<any>) => {
    if (isPrimitiveSchema(arraySchema._def.type)) {
      return [];
    }

    return [processSchemaField(arraySchema._def.type)];
  };

  const getLiteralDefault = (literalSchema: z.ZodLiteral<any>) => literalSchema._def.value;

  const getUnionDefault = (unionSchema: z.ZodUnion<any>) => {
    if (Array.isArray(unionSchema._def.options)) {
      const defaultSchema = unionSchema._def.options.find(s => s instanceof z.ZodLiteral);
      if (defaultSchema) {
        return getLiteralDefault(defaultSchema);
      }

      for (let i=0; i<unionSchema._def.options.length; i++) {
        const parsableValue = processSchemaField(unionSchema._def.options[i]);
        if (parsableValue) {
          return parsableValue;
        }
      }
    }

    return undefined;
  };

  const processSchemaField = (field: z.ZodTypeAny) => {
    if (field instanceof z.ZodDefault) {
      return getSchemaDefault(field);
    }

    if (field instanceof z.ZodArray) {
      return getArrayDefault(field);
    }

    if (field instanceof z.ZodObject) {
      return getObjectDefault(field);
    }

    if (field instanceof z.ZodBoolean) {
      return false;
    }

    if (field instanceof z.ZodOptional) {
      return processSchemaField(field._def.innerType);
    }

    if (field instanceof z.ZodNullable) {
      return null;
    }

    if (field instanceof z.ZodLiteral) {
      return getLiteralDefault(field);
    }

    if (field instanceof z.ZodUnion) {
      return getUnionDefault(field);
    }

    if (field instanceof z.ZodString) {
      return undefined;
    }

    if (field instanceof z.ZodEffects) {
      const extracted = extractSchema(field);
      return extracted ? processSchemaField(extracted) : undefined;
    }

    if (import.meta.env.DEV) {
      console.warn('createObjectFromSchema encountered a schema field type it wasn\'t able to process. A case to handle this should likely be added above', field);
    }

    return undefined;
  };

  const useOverride = (key: string) => {
    if (!overrides) return false;
    return (key in overrides);
  };

  const result = Object.fromEntries(
    Object.keys(_schema.shape).map(key => {
      const userProvided = useOverride(key);
      const value = userProvided ? overrides[key] : processSchemaField(_schema.shape[key]);
      if (!userProvided && typeof value === 'object' && !Array.isArray(value) && value !== null && addGeneratedIdentifier) {
        value.generated = true;
      }

      if (Array.isArray(value) && !userProvided) {
        for (const item of value) {
          if (typeof item === 'object' && !Array.isArray(item) && item !== null) {
            item.generated = true;
          }
        }
      }
      return [key, value];
    }),
  ) as ResultObject<T>;

  if (addGeneratedIdentifier) {
    result.generated = true;
  }


  return result;
};

export const isValueValid = <T extends z.ZodTypeAny>(schema: T, test: unknown): boolean => {
  try {
    schema.parse(test);
    return true;
  } catch {
    return false;
  }
};

export const removeObjectsWithoutIds = <T extends object>(obj: T) => Object.fromEntries(
  Object.entries(obj).filter(([, value]) =>
    typeof value !== 'object' ||
    Array.isArray(value) ||
    (typeof value === 'object' && ('id' in value) && value.id !== undefined)),
) as T;

export const removeUndefinedFields = <T extends object>(obj: T) => Object.fromEntries(
  Object.entries(obj).filter(([, value]) => value !== undefined),
) as T;

export const removeEmptyArrays = <T extends object>(obj: T) => Object.fromEntries(
  Object.entries(obj).filter(([, value]) => !Array.isArray(value) || (Array.isArray(value) && value.length)),
) as T;

export const removeArrayElements = <T extends object>(obj: T, predicate: (key: string, val: any) => () => boolean) => Object.fromEntries(
  Object.entries(obj).filter(([k, value]) => {
    if (!Array.isArray(value)) return value;
    const filterFn = predicate(k, value);
    return value.filter(filterFn);
  }),
) as T;

export const transformUndefinedToNull = <T extends object>(obj: T) => Object.fromEntries(
  Object.keys(obj).map(key => [key, obj[key] === undefined ? null : obj[key]]),
);
